import React, { useEffect, useState } from "react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
  animate as fmAnimate,
  useReducedMotion,
} from "framer-motion";
import { YARNS, YARN_ORDER } from "../lib/yarns";

/**
 * PullTassel — the prominent, enticing pull element that lives BELOW the loom.
 *
 * A multi-coloured braided cord descends to a tassel knot with a fringe of
 * yarn threads. Clicking, tapping, focusing+Enter, or dragging downward will
 * trigger onPull(). After the third pull, the title shifts to invite the
 * Monday-note signup.
 */
export default function PullTassel({
  onPull,
  isPulling = false,
  pullCount = 0,
  lastYarn = null,
}) {
  const reducedMotion = useReducedMotion();
  const [armed, setArmed] = useState(false);
  const dragY = useMotionValue(0);
  const stretch = useTransform(dragY, [0, 120], [1, 1.3]);

  // After a brief delay, "arm" the tassel — adds a soft glow + ripple to
  // make it feel inviting without being noisy.
  useEffect(() => {
    if (isPulling || pullCount > 0) {
      setArmed(true);
      return;
    }
    const t = setTimeout(() => setArmed(true), 1200);
    return () => clearTimeout(t);
  }, [isPulling, pullCount]);

  function trigger() {
    if (isPulling) return;
    onPull?.();
  }

  function handleDragEnd(_, info) {
    if (info.offset.y > 50) {
      fmAnimate(dragY, 0, { duration: 0.45, ease: "easeOut" });
      trigger();
    } else {
      fmAnimate(dragY, 0, { duration: 0.3, ease: "easeOut" });
    }
  }

  const accent = lastYarn ? YARNS[lastYarn].accent : "#8a6f44";

  const headline =
    pullCount === 0
      ? "Pull me now!"
      : pullCount === 1
      ? "Pull me again!"
      : pullCount === 2
      ? "One more pull?"
      : "That's today's loom.";

  const lede =
    pullCount === 0
      ? "Which thread are you going to tug on today?"
      : pullCount === 1
      ? "One thread, drawn. The loom is generous — try another."
      : pullCount === 2
      ? "Two threads. The order is the loom's, not yours."
      : "Three threads drawn. The loom rests until tomorrow.";

  const sub =
    pullCount === 0
      ? "Click the tassel below — or focus it and press Enter."
      : pullCount === 1
      ? "Each pull draws from a different yarn."
      : pullCount === 2
      ? "One more if the day asks for it."
      : "Fresh threads tomorrow. Pin the ones you want to keep — they live on your loomboard.";

  return (
    <section
      className="relative pt-6 pb-2"
      data-testid="pull-tassel-section"
      aria-labelledby="pull-tassel-heading"
    >
      <div className="max-w-3xl mx-auto px-6 text-center">
        <p
          className="text-base md:text-lg text-[#3a3a3a]/85 leading-snug max-w-xl mx-auto italic"
          data-testid="pull-tassel-lede"
        >
          {lede}
        </p>

        <h2
          id="pull-tassel-heading"
          className="relative inline-block mt-4 text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight text-[#1a1a1a] leading-[0.95]"
          style={{ fontFamily: "Rubik, sans-serif" }}
          data-testid="pull-tassel-headline"
        >
          {headline}
          {/* Hand-drawn squiggle underline */}
          <svg
            className="absolute left-0 right-0 -bottom-3 md:-bottom-4 w-full"
            viewBox="0 0 400 22"
            preserveAspectRatio="none"
            aria-hidden
            style={{ height: "0.42em" }}
          >
            <path
              d="M 4 14 Q 50 4, 100 12 T 200 11 T 300 13 T 396 9"
              fill="none"
              stroke="#e93e36"
              strokeWidth="4"
              strokeLinecap="round"
            />
          </svg>
        </h2>

        <p className="mt-7 text-sm md:text-base text-[#3a3a3a]/75 leading-relaxed max-w-md mx-auto">
          {sub}
        </p>

        <div className="relative mx-auto mt-7 mb-2 flex flex-col items-center">
          {/* Soft halo behind the tassel — felt, not seen */}
          <motion.span
            aria-hidden
            className="absolute top-0 w-44 h-44 rounded-full"
            style={{
              backgroundColor: lastYarn ? YARNS[lastYarn].pastel : "#fae2b5",
              filter: "blur(38px)",
              opacity: 0.55,
              zIndex: 0,
            }}
            animate={
              reducedMotion ? undefined : { opacity: armed ? [0.4, 0.7, 0.4] : 0.3 }
            }
            transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
          />

          {/* "Click here to pull" callout — visible only before first pull,
              points directly at the tassel so the gesture is unambiguous. */}
          <AnimatePresence>
            {pullCount === 0 && armed && !isPulling && (
              <motion.div
                key="pull-callout"
                initial={{ opacity: 0, y: -8 }}
                animate={
                  reducedMotion
                    ? { opacity: 1, y: 0 }
                    : { opacity: 1, y: [0, 6, 0] }
                }
                exit={{ opacity: 0, y: -8 }}
                transition={
                  reducedMotion
                    ? { duration: 0.4 }
                    : {
                        opacity: { duration: 0.5 },
                        y: { duration: 1.4, repeat: Infinity, ease: "easeInOut" },
                      }
                }
                className="relative z-20 mb-3 pointer-events-none"
                data-testid="pull-callout"
                aria-hidden
              >
                <div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-semibold shadow-[0_6px_18px_rgba(233,62,54,0.35)] border-2"
                  style={{
                    backgroundColor: "#e93e36",
                    borderColor: "#3d2a14",
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
                    <circle cx="8" cy="8" r="4" fill="#ffffff" />
                    <circle cx="8" cy="8" r="6.5" fill="none" stroke="#ffffff" strokeWidth="1.4" opacity="0.7" />
                  </svg>
                  Click here to pull
                </div>
                {/* hand-drawn arrow pointing down at the tassel */}
                <svg
                  className="block mx-auto"
                  width="42"
                  height="40"
                  viewBox="0 0 42 40"
                  aria-hidden
                >
                  <path
                    d="M 21 2 C 22 12, 18 22, 21 32 M 14 26 L 21 34 L 28 26"
                    stroke="#e93e36"
                    strokeWidth="2.6"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.button
            type="button"
            data-testid="pull-tassel-button"
            aria-label={
              pullCount < 3
                ? "Pull the loom's loose thread to draw a card"
                : "Pull one more thread"
            }
            onClick={trigger}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                trigger();
              }
            }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 130 }}
            dragElastic={0.15}
            dragMomentum={false}
            style={{ y: dragY, scaleY: stretch }}
            onDragEnd={handleDragEnd}
            disabled={isPulling}
            whileHover={reducedMotion ? undefined : { scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            animate={
              reducedMotion || isPulling
                ? undefined
                : { rotate: [-2.2, 2.2, -2.2] }
            }
            transition={{
              rotate: { duration: 5.5, repeat: Infinity, ease: "easeInOut" },
            }}
            className="relative z-10 cursor-grab active:cursor-grabbing focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-4 focus-visible:ring-offset-weave-paper rounded-full disabled:opacity-80"
            // origin at top so the sway looks like it's hanging from above
          >
            <Tassel armed={armed} accent={accent} isPulling={isPulling} />
          </motion.button>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: armed ? 1 : 0 }}
            transition={{ duration: 0.6 }}
            className="mt-4 z-10"
          >
            <span
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1a1a1a] text-white text-sm font-medium shadow-[0_6px_18px_rgba(0,0,0,0.18)]"
              data-testid="pull-tassel-cta"
            >
              {isPulling ? (
                <>
                  <span className="inline-block w-2 h-2 bg-weave-yellow rounded-full animate-pulse" />
                  Drawing the thread…
                </>
              ) : (
                <>
                  Pull the thread
                  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
                    <path
                      d="M7 2v8M3 7l4 4 4-4"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </>
              )}
            </span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/**
 * Tassel — pure SVG: braided cord + knot + fringe in the four yarn colours.
 */
function Tassel({ armed, accent, isPulling }) {
  // Fringe threads: alternating yarn colours, slight randomised endpoints
  const fringeColors = [
    YARNS.centre.deep,
    YARNS.sharpen.deep,
    YARNS.reflect.deep,
    YARNS.act.deep,
    YARNS.centre.deep,
    YARNS.sharpen.deep,
    YARNS.reflect.deep,
    YARNS.act.deep,
    YARNS.centre.deep,
    YARNS.sharpen.deep,
    YARNS.reflect.deep,
    YARNS.act.deep,
    YARNS.centre.deep,
    YARNS.sharpen.deep,
  ];

  return (
    <svg
      viewBox="0 0 220 320"
      width="200"
      height="290"
      role="presentation"
      aria-hidden="true"
      style={{ overflow: "visible" }}
    >
      <defs>
        <linearGradient id="cord-grad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#a98a55" />
          <stop offset="100%" stopColor="#8a6f44" />
        </linearGradient>
        <radialGradient id="knot-grad" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#d6b376" />
          <stop offset="100%" stopColor="#8a6f44" />
        </radialGradient>
        <filter id="t-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
          <feOffset dx="0" dy="4" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.22" />
          </feComponentTransfer>
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Braided cord — three intertwined strands in different yarn colours */}
      <g>
        {YARN_ORDER.slice(0, 3).map((k, i) => {
          const yarn = YARNS[k];
          // Three strands twist around each other via a sine path
          const phase = i * (Math.PI * 2) / 3;
          const points = Array.from({ length: 12 }).map((_, j) => {
            const t = j / 11;
            const x = 110 + Math.sin(phase + t * Math.PI * 4) * 6;
            const y = 4 + t * 120;
            return [x, y];
          });
          const d =
            `M ${points[0][0]} ${points[0][1]} ` +
            points
              .slice(1)
              .map(([x, y]) => `L ${x.toFixed(1)} ${y.toFixed(1)}`)
              .join(" ");
          return (
            <path
              key={k}
              d={d}
              stroke={yarn.deep}
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
              opacity="0.92"
            />
          );
        })}
      </g>

      {/* Bind ring at the bottom of the cord */}
      <ellipse
        cx="110"
        cy="128"
        rx="20"
        ry="6"
        fill={accent}
        opacity="0.85"
      />

      {/* Tassel knot — the fat head */}
      <g filter="url(#t-shadow)">
        <path
          d="M 76 130
             C 70 150, 70 170, 84 178
             L 136 178
             C 150 170, 150 150, 144 130
             Z"
          fill="url(#knot-grad)"
          stroke="#6a5232"
          strokeWidth="1"
        />
        {/* Wrapping coils on the knot */}
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <path
            key={i}
            d={`M ${78 + i * 0.6} ${138 + i * 7} Q 110 ${136 + i * 7}, ${142 - i * 0.6} ${138 + i * 7}`}
            stroke="#6a5232"
            strokeWidth="0.9"
            fill="none"
            opacity="0.55"
          />
        ))}
      </g>

      {/* Fringe — many threads in yarn colours hanging below the knot */}
      <g filter="url(#t-shadow)">
        {fringeColors.map((color, i) => {
          const x = 80 + i * 4.5;
          const len = 96 + ((i * 13) % 24);
          // Slight curl on fringe ends
          const curl = ((i % 3) - 1) * 4;
          return (
            <path
              key={i}
              d={`M ${x} 178 Q ${x + curl} ${178 + len * 0.5}, ${x + curl} ${178 + len}`}
              stroke={color}
              strokeWidth="2.4"
              fill="none"
              strokeLinecap="round"
              opacity="0.92"
            />
          );
        })}
      </g>

      {/* Subtle highlight on the cord left side */}
      <path
        d="M 102 12 Q 102 70, 102 124"
        stroke="#ffffff"
        strokeWidth="1"
        fill="none"
        opacity="0.25"
        strokeLinecap="round"
      />

      {/* When isPulling, draw a tug-line going up from the knot */}
      {isPulling && (
        <motion.path
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1, y: -10 }}
          transition={{ duration: 0.5 }}
          d="M 110 0 L 110 130"
          stroke={accent}
          strokeWidth="3"
          strokeDasharray="3 4"
          fill="none"
          strokeLinecap="round"
        />
      )}

      {/* Armed pulse — a quiet ring suggesting "ready to pull" */}
      {armed && !isPulling && (
        <motion.circle
          cx="110"
          cy="154"
          r="46"
          fill="none"
          stroke={accent}
          strokeWidth="1.2"
          opacity="0.35"
          animate={{ r: [46, 60, 46], opacity: [0.35, 0, 0.35] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
        />
      )}
    </svg>
  );
}
